import { h } from 'preact';
import { useContext, useState, useEffect } from 'preact/hooks';
import classNames from 'classnames';

import {
  getLastMessageDisplayData,
  setKustomerLocalStorage,
} from 'widget_main/globals/helpers';
import {
  NavigationContext,
  ConversationsContext,
  SettingsContext,
  UnreadCountsContext,
  MessagesContext,
  ChatAvailabilityContext,
  HideNewConversationButtonSettingsContext,
} from 'widget_main/store';
import {
  CONVERSATIONS_PATH,
  MESSAGE_THREAD_PATH,
  WIDGET_TYPE_CHAT_KB,
} from 'widget_main/globals/constants';
import * as keyboardEventHelpers from 'widget_main/globals/keyboardEvents';

import ConversationsEmpty from 'widget_main/globals/assets/conversationsEmpty.svg';
import Offline from 'widget_main/globals/assets/offline.svg';
import Avatar from 'widget_main/components/Avatar';
import TimeAgo from 'widget_main/components/TimeAgo';
import Unread from 'widget_main/components/Unread';
import NewConversationButton from 'widget_main/components/Button/NewConversationButton';

import { ConversationEntity } from 'widget_main/store/types';

import { useTranslations } from 'i18n/hooks';
import { getHTMLFromMarkdown } from 'widget_main/components/MessageThread/ChatMessage/helpers';
import messages from './messages';

import styles from './conversations.scss';

const Conversations = () => {
  const chatAvailability = useContext(ChatAvailabilityContext);
  const hideNewConversationButtonSettings = useContext(
    HideNewConversationButtonSettingsContext,
  );
  const navigation = useContext(NavigationContext);
  const conversations = useContext(ConversationsContext);
  const messagesState = useContext(MessagesContext);
  const unreadCounts = useContext(UnreadCountsContext);
  const chatSettings = useContext(SettingsContext);

  const translations = useTranslations(messages);

  const [, forceUpdate] = useState({});

  useEffect(() => {
    setKustomerLocalStorage('lastOpenPage', CONVERSATIONS_PATH);

    const interval = setInterval(() => {
      forceUpdate({});
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleClickConversation = (e, id) => {
    e.preventDefault();
    e.stopPropagation();
    navigation.updateCurrentConversationId(id);
    navigation.updatePage(MESSAGE_THREAD_PATH);
  };

  const handleKeyPress = (e, id) => {
    if (keyboardEventHelpers.isEnterKey(e)) {
      handleClickConversation(e, id);
    }
  };

  const getFilteredConversations = () => {
    const conversationsList = Object.values(conversations.data);

    if (chatSettings.noHistory) {
      return conversationsList.filter((conversation) => !conversation.ended);
    }
    return conversationsList;
  };

  const getSortedConversations = () => {
    const filteredConversations = getFilteredConversations();

    return filteredConversations.sort((a, b) => {
      const timestamp1 = a.lastMessageAt
        ? new Date(a.lastMessageAt).getTime()
        : 0;

      const timestamp2 = b.lastMessageAt
        ? new Date(b.lastMessageAt).getTime()
        : 0;

      if (timestamp1 > timestamp2) {
        return -1;
      }
      return 1;
    });
  };

  const renderEmpty = () => {
    if (chatAvailability === 'online') {
      return (
        <div className={styles.empty}>
          <img
            alt={translations.conversationsEmptyAltText}
            src={ConversationsEmpty}
            className={styles.conversationsEmpty}
          />
          <h4 className={styles.emptyTitle}>{translations.emptyTitle}</h4>
          <p className={styles.emptySubtitle}>{translations.emptySubtitle}</p>
        </div>
      );
    }

    return (
      <div className={styles.empty}>
        {chatSettings?.offhoursImageUrl ? (
          <img
            className={styles.offlineCustomImage}
            alt={translations.offHoursAltText}
            src={chatSettings?.offhoursImageUrl}
          />
        ) : (
          <img alt={translations.offlineAltText} src={Offline} />
        )}
        <h4 className={styles.emptyTitle}>{translations.offHoursEmptyTitle}</h4>
        <p className={styles.emptySubtitle}>
          {translations.offHoursEmptySubtitle}
        </p>
      </div>
    );
  };

  const renderPreviewText = (previewText?) => {
    if (!previewText) {
      return null;
    }

    const html = getHTMLFromMarkdown(previewText, { breaks: false }).replace(
      /<img.*>/g,
      translations.previewAttachmentReceived,
    );

    return (
      <span
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: html,
        }}
      />
    );
  };

  const renderConversationItem = (conversation: ConversationEntity) => {
    const { conversationId, lastMessageAt, ended } = conversation;

    const lastMessageDisplayData = getLastMessageDisplayData(
      conversation,
      messagesState,
      chatSettings,
      translations,
    );

    return (
      <div
        role="button"
        tabIndex={0}
        key={conversationId}
        class={classNames(styles.conversation, {
          [styles.unreadConversation]: !!unreadCounts.conversations[
            conversationId
          ],
          [styles.endedConversation]: ended,
        })}
        onKeyPress={(e) => handleKeyPress(e, conversationId)}
        onClick={(e) => handleClickConversation(e, conversationId)}
      >
        <Avatar
          className={styles.avatar}
          size={40}
          avatarUrl={lastMessageDisplayData.avatarUrl}
        />
        <div class={styles.conversationDetails}>
          <div class={styles.conversationHeader}>
            <div class={styles.agentName}>
              {lastMessageDisplayData.displayName}
            </div>
            {ended && (
              <div className={styles.timestamp}>{translations.ended}</div>
            )}
            {!ended && lastMessageAt && (
              <TimeAgo className={styles.timestamp} timestamp={lastMessageAt} />
            )}
          </div>
          <div class={styles.conversationBody}>
            <div class={styles.preview}>
              {renderPreviewText(lastMessageDisplayData.previewText)}
            </div>
            <Unread
              className={styles.unreadProp}
              conversationId={conversationId}
            />
          </div>
        </div>
      </div>
    );
  };

  const renderConversationsList = () => {
    const conversationsList = getSortedConversations();

    return (
      <div class={styles.conversationList}>
        {conversationsList.map(renderConversationItem)}
        {conversationsList.length === 0 && renderEmpty()}
      </div>
    );
  };

  const renderNewConversationButton = () => {
    const openConversationFound = Object.values(conversations.data).find(
      (conversation) => !conversation.ended,
    );

    if (
      hideNewConversationButtonSettings.onChatHistory ||
      (chatSettings.singleSessionChat && openConversationFound)
    ) {
      return null;
    }

    return (
      <NewConversationButton
        className={classNames(styles.createConversationButton, {
          [styles.createConversationButtonWithNavigation]:
            chatSettings.widgetType === WIDGET_TYPE_CHAT_KB,
        })}
      />
    );
  };

  return (
    <div class={styles.container}>
      {renderConversationsList()}
      {renderNewConversationButton()}
    </div>
  );
};

export default Conversations;
